import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import URL from "../../../../Main/url";
import CurrencyWord from "../../../../Componets/Currencywords";
import UserCurrency from "../../../../Componets/UserCurrency";

const A4Invoice = React.forwardRef((props, ref) => {
  const { values } = props;

  const headerRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [tableHeaderHeight, setTableHeaderHeight] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const itemsPerPage = 10;

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getItemsForPage = (page) => {
    if (!values?.details) return [];
    const start = (page - 1) * itemsPerPage;
    return values.details.slice(start, start + itemsPerPage);
  };

  // ── Dynamic header measurement ──────────────────────────────────────────────
  useEffect(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    if (tableHeaderRef.current)
      setTableHeaderHeight(tableHeaderRef.current.offsetHeight);
  }, [values]);

  // ── Page count ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (values?.details) {
      setTotalPages(Math.ceil(values.details.length / itemsPerPage));
    }
  }, [values?.details]);

  // ── Inject print styles with measured heights ────────────────────────────────
  const injectScopedStyles = (headerH, tableHeaderH) => {
    const existing = document.getElementById("a4-billing-invoice-styles");
    if (existing) existing.remove();

    const style = document.createElement("style");
    style.id = "a4-billing-invoice-styles";

    const contentMargin = headerH + tableHeaderH;

    style.innerHTML = `
      @page {
        size: A4;
        margin: 3% 6% 5% 6%;
      }

      @media print {
        .header-section {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: white;
          z-index: 1000;
          page-break-inside: avoid;
        }

        .table-header-section {
          position: fixed;
          top: ${headerH}px;
          left: 0;
          right: 0;
          background: white;
          z-index: 999;
          page-break-inside: avoid;
        }

        .invoice-content {
          margin-top: ${contentMargin}px;
        }

        .page-break {
          page-break-before: always;
          margin-top: ${contentMargin}px;
        }
      }

      .invoice-table {
        table-layout: fixed;
        width: 100%;
      }

      .col-sno   { width: 5%; }
      .col-desc  { width: 40%; }
      .col-unit  { width: 10%; }
      .col-qty   { width: 10%; }
      .col-price { width: 12%; }
      .col-vat   { width: 13%; }
      .col-total { width: 10%; }
    `;
    document.head.appendChild(style);
  };

  useEffect(() => {
    if (values?.branch?.name && headerHeight && tableHeaderHeight) {
      injectScopedStyles(headerHeight, tableHeaderHeight);
    }
  }, [values?.branch?.name, headerHeight, tableHeaderHeight]);

  // ── Header ───────────────────────────────────────────────────────────────────
  const renderHeaderSection = () => (
    <div className="header-section" ref={headerRef}>
      <div className="flex justify-between items-start">
        <div className="text-black-900 text-[0.65rem]">
          <div className="font-semibold text-md">
            {capitalizeWords(values?.branch?.name)}
          </div>
          <div className="text-xs">
            Email : {values?.branch?.email || ""}
          </div>
          <div className="text-xs">
            Phone : {values?.branch?.phone || ""}
          </div>
          <div className="text-xs">
            {values?.branch?.area ? values?.branch?.area + ", " : ""}
            {values?.branch?.city ? values?.branch?.city + ", " : ""}
            {values?.branch?.country || ""}
          </div>
          <div className="text-xs">
            VATIN : {values?.branch?.tax || "N/A"}
          </div>
        </div>

        {values?.branch?.image ? (
          <div className="flex flex-col items-center">
            <img
              className="w-[50px] h-[50px]"
              src={`${URL}/uploads${values?.branch?.image}`}
              alt="Branch Logo"
            />
            <div className="font-semibold text-lg">
              {capitalizeWords(values?.branch?.name)}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-[50px] h-[50px] rounded-md bg-gray-300 text-black flex items-center justify-center text-xl font-semibold">
              {values?.branch?.name
                ? values.branch.name.charAt(0).toUpperCase()
                : "N"}
            </div>
            <div className="font-semibold text-lg">
              {capitalizeWords(values?.branch?.name)}
            </div>
          </div>
        )}
      </div>

      <hr className="my-2 border-black" />
      <div className="text-center text-md py-1 font-semibold">TAX INVOICE</div>
      <hr className="my-2 border-black" />

      <div className="border border-gray-300 rounded-lg p-4 mb-2 shadow-sm">
        <div className="flex justify-between">
          {/* ── Customer info ── */}
          <div>
            <div className="font-semibold text-[0.70rem] mb-2">
              Customer Name
            </div>
            <div className="text-[0.65rem] space-y-1">
              <div className="font-medium">
                {values?.customer?.name || "Walk-in Customer"}
              </div>
              {values?.customer?.phone && (
                <div>
                  <span className="font-semibold">Phone :</span>{" "}
                  {values?.customer?.phone}
                </div>
              )}
              {values?.customer?.email && (
                <div>
                  <span className="font-semibold">Email :</span>{" "}
                  {values?.customer?.email}
                </div>
              )}
              <div>
                {values?.customer?.city
                  ? values?.customer?.city + ", "
                  : ""}
                {values?.customer?.state}
              </div>
              <div>{values?.customer?.country}</div>
              {values?.customer?.tax && (
                <div>
                  <span className="font-semibold">VATIN :</span>{" "}
                  {values?.customer?.tax}
                </div>
              )}
            </div>
          </div>

          {/* ── Invoice meta ── */}
          <div>
            <div className="text-[0.65rem] space-y-1">
              <div className="flex">
                <div className="w-24 font-bold">Invoice No</div>
                <div className="px-2">:</div>
                <div>#INV-{values?.number}</div>
              </div>
              <div className="flex">
                <div className="w-24 font-bold">Invoice Type</div>
                <div className="px-2">:</div>
                <div>
                  {values?.payment_status == 2
                    ? values?.payments
                      ?.map((v, i) => (i > 0 ? ", " : "") + v?.name)
                      .join("")
                    : "CREDIT"}
                </div>
              </div>
              <div className="flex">
                <div className="w-24 font-bold">Date</div>
                <div className="px-2">:</div>
                <div>{moment(values?.date).format("DD-MM-YYYY")}</div>
              </div>
              {values?.due_date && (
                <div className="flex">
                  <div className="w-24 font-bold">Due Date</div>
                  <div className="px-2">:</div>
                  <div>{moment(values?.due_date).format("DD-MM-YYYY")}</div>
                </div>
              )}
              {values?.lpo_number && (
                <div className="flex">
                  <div className="w-24 font-bold">LPO No</div>
                  <div className="px-2">:</div>
                  <div>{values?.lpo_number}</div>
                </div>
              )}
              {values?.lpo_date && (
                <div className="flex">
                  <div className="w-24 font-bold">LPO Date</div>
                  <div className="px-2">:</div>
                  <div>{moment(values?.lpo_date).format("DD-MM-YYYY")}</div>
                </div>
              )}
              {values?.salesman?.name && (
                <div className="flex">
                  <div className="w-24 font-bold">Salesman</div>
                  <div className="px-2">:</div>
                  <div>{values?.salesman?.name}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Table headers ─────────────────────────────────────────────────────────────
  const renderTableHeaders = () => (
    <div className="table-header-section pt-[8px]" ref={tableHeaderRef}>
      <table className="invoice-table w-full border-separate border-spacing-0 rounded-t-lg overflow-hidden border border-gray-300 mb-0">
        <thead>
          <tr className="bg-gray-100">
            <th className="col-sno text-[0.60rem] text-left py-2 pl-3 border-b border-gray-300 rounded-tl-lg">
              S.NO
            </th>
            <th className="col-desc text-[0.60rem] text-left p-1 border-b border-gray-300">
              DESCRIPTION
            </th>
            <th className="col-unit text-[0.60rem] text-center p-1 border-b border-gray-300">
              UNIT
            </th>
            <th className="col-price text-[0.60rem] text-center p-1 border-b border-gray-300">
              PRICE
            </th>
            <th className="col-qty text-[0.60rem] text-center p-1 border-b border-gray-300">
              QTY
            </th>
            <th className="col-vat text-[0.60rem] text-center p-1 border-b border-gray-300">
              VAT
            </th>
            <th className="col-total text-[0.60rem] text-center pr-2 border-b border-gray-300 rounded-tr-lg">
              TOTAL
            </th>
          </tr>
        </thead>
      </table>
    </div>
  );

  // ── Table body (per page) ─────────────────────────────────────────────────────
  const renderTableBody = (pageItems, pageNumber, isLastPage) => (
    <div
      key={`page-${pageNumber}`}
      className={pageNumber > 1 ? "page-break" : "invoice-content"}
    >
      <table className="invoice-table w-full border-separate border-spacing-0 overflow-hidden border border-gray-300 border-t-0">
        <tbody className="text-sm">
          {pageItems.map((value, index) => {
            const globalIndex = (pageNumber - 1) * itemsPerPage + index;
            const isLastOverallRow =
              isLastPage && index === pageItems.length - 1;

            return (
              <tr
                key={`${pageNumber}-${index}`}
                className="hover:bg-gray-50"
              >
                <td
                  className={`col-sno text-[0.60rem] p-1 pl-3 ${isLastOverallRow ? "rounded-bl-lg" : ""
                    }`}
                >
                  {globalIndex + 1}
                </td>
                <td className="col-desc text-[0.60rem] p-1">
                  {value?.name}
                </td>
                <td className="col-unit text-[0.60rem] text-center p-1">
                  {value?.unit_name}
                </td>
                <td className="col-price text-[0.60rem] text-center p-1">
                  {value?.sale_price?.toFixed(3)}
                </td>
                <td className="col-qty text-[0.60rem] text-center p-1">
                  {value?.quantity?.toFixed(3)}
                </td>
                <td className="col-vat text-[0.60rem] text-center p-1">
                  <div>{value?.tax}%</div>
                  <div className="text-gray-500">
                    {value?.tax_amount?.toFixed(3)}
                  </div>
                </td>
                <td
                  className={`col-total text-[0.60rem] text-center pr-2 ${isLastOverallRow ? "rounded-br-lg" : ""
                    }`}
                >
                  {value?.total?.toFixed(3)}
                </td>
              </tr>
            );
          })}

          {/* ── Empty padding rows ── */}
          {pageItems.length < itemsPerPage &&
            Array.from({ length: itemsPerPage - pageItems.length }).map(
              (_, index, arr) => {
                const lastRowRounded =
                  isLastPage && index === arr.length - 1;
                return (
                  <tr
                    key={`empty-${pageNumber}-${index}`}
                    className="hover:bg-gray-50"
                  >
                    <td
                      className={`text-[0.60rem] p-2 pl-3 ${lastRowRounded ? "rounded-bl-lg" : ""
                        }`}
                    >
                      &nbsp;
                    </td>
                    <td className="p-2">&nbsp;</td>
                    <td className="p-2">&nbsp;</td>
                    <td className="p-2">&nbsp;</td>
                    <td className="p-2">&nbsp;</td>
                    <td className="p-2">&nbsp;</td>
                    <td
                      className={`text-[0.60rem] p-2 pr-2 ${lastRowRounded ? "rounded-br-lg" : ""
                        }`}
                    >
                      &nbsp;
                    </td>
                  </tr>
                );
              }
            )}
        </tbody>
      </table>

      {/* ── Footer: totals + bank details (last page only) ── */}
      {isLastPage && (
        <div className="border border-gray-300 rounded-lg p-4 mb-2 mt-4 shadow-sm">
          <div className="flex justify-between">
            {/* Left: words + bank */}
            <div className="text-[0.65rem] w-1/2">
              <div className="font-semibold mb-2 text-[0.70rem]">
                Total in words&nbsp;:&nbsp;
                <span className="font-normal">
                  <CurrencyWord amount={values?.total?.toFixed(3)} />
                </span>
              </div>

              {(values?.branch?.bank_name ||
                values?.branch?.bank_swift ||
                values?.branch?.bank_swift) && (
                  <div className="mt-3 space-y-1">
                    {values?.branch?.bank_name && (
                      <div className="flex gap-2">
                        <span className="font-semibold w-24">Bank Name</span>
                        <span>: {values?.branch?.bank_name}</span>
                      </div>
                    )}
                    {values?.branch?.bank_branch && (
                      <div className="flex gap-2">
                        <span className="font-semibold w-24">Branch</span>
                        <span>: {values?.branch?.bank_branch}</span>
                      </div>
                    )}
                    {values?.branch?.bank_account_name && (
                      <div className="flex gap-2">
                        <span className="font-semibold w-24">Account Name</span>
                        <span>: {values?.branch?.bank_account_name}</span>
                      </div>
                    )}
                    {values?.branch?.bank_account_number && (
                      <div className="flex gap-2">
                        <span className="font-semibold w-24">Account No</span>
                        <span>: {values?.branch?.bank_account_number}</span>
                      </div>
                    )}
                    {values?.branch?.bank_swift && (
                      <div className="flex gap-2">
                        <span className="font-semibold w-24">Swift Code</span>
                        <span>: {values?.branch?.bank_swift}</span>
                      </div>
                    )}
                  </div>
                )}
            </div>

            {/* Right: summary table */}
            <div className="text-[0.65rem] w-1/2">
              <table className="w-full text-[0.65rem]">
                <tbody>
                  <tr>
                    <td className="text-right pr-2 py-1">Subtotal</td>
                    <td className="text-right w-20 py-1">
                      {values?.subtotal?.toFixed(3)}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">VAT</td>
                    <td className="text-right py-1">
                      {values?.taxamount?.toFixed(3)}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">Discount</td>
                    <td className="text-right py-1">
                      {values?.discount_type
                        ? values?.discount_value
                          ? `${values?.discount?.toFixed(3)} (-${values?.discount_value}%)`
                          : "0.000"
                        : values?.discount
                          ? `-${values?.discount?.toFixed(3)}`
                          : "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">Delivery Charges</td>
                    <td className="text-right py-1">
                      {values?.delivery?.toFixed(3) || "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-2 font-semibold">
                      Total (<UserCurrency />)
                    </td>
                    <td className="text-right py-2 font-semibold">
                      {values?.total?.toFixed(3)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Note ── */}
      {isLastPage && values?.note && (
        <div
          className="pt-4 text-regular text-sm"
          dangerouslySetInnerHTML={{ __html: values?.note }}
        />
      )}
    </div>
  );

  return (
    <div className="a4-invoice" ref={ref}>
      <style>{`
        @media print {
          .a4-invoice { page-break-before: always; }
          @page { size: A4; margin: 3% 6% 5% 6%; }
        }
        @media screen {
          .header-section       { position: static; }
          .table-header-section { position: static; }
          .invoice-content      { margin-top: 0; }
          .page-break           { margin-top: 0; page-break-before: auto; }
        }
      `}</style>

      {renderHeaderSection()}
      {renderTableHeaders()}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
        (pageNumber) => {
          const pageItems = getItemsForPage(pageNumber);
          const isLastPage = pageNumber === totalPages;
          return renderTableBody(pageItems, pageNumber, isLastPage);
        }
      )}
    </div>
  );
});

export default A4Invoice;